import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Lock } from 'lucide-react'

import { ApiError } from '@/api/client'
import { roomBookingsApi, roomsApi, scheduleApi } from '@/api/endpoints'
import type { Lesson, RoomBooking } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { canManageRoomBookings } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert } from '@/components/ui/Alert'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { FullScreenLoader } from '@/components/ui/Spinner'
import { cn } from '@/lib/cn'
import {
  addDays,
  endOfWeek,
  formatDayShort,
  isToday,
  startOfWeek,
  toIsoDate,
  weekDays,
} from '@/lib/scheduleTime'
import { LessonDetailModal } from '../schedule/LessonDetailModal'
import { LessonFormModal } from '../schedule/LessonFormModal'
import { RoomBookingDetailModal } from '../schedule/RoomBookingDetailModal'
import { RoomBookingFormModal } from '../schedule/RoomBookingFormModal'
import { LegendaProwadzacych, TimeGrid } from '../schedule/TimeGrid'
import type { GridColumn } from '../schedule/TimeGrid'

/**
 * Dwa ujęcia tej samej sali, oba na wspólnej osi godzin.
 *
 * „Ogólny” to obsada — jeden blok na nauczyciela i dzień, od pierwszej do
 * ostatniej lekcji. Tak wyglądał arkusz, którym szkoła posługiwała się przed
 * wdrożeniem: każda zakładka opisywała jedną salę. Dlatego to on otwiera się
 * pierwszy. „Szczegółowy” rozbija ten blok na poszczególne rezerwacje.
 */
type Ujecie = 'ogolny' | 'szczegolowy'

const UJECIA: { value: Ujecie; label: string }[] = [
  { value: 'ogolny', label: 'Ogólny' },
  { value: 'szczegolowy', label: 'Szczegółowy' },
]

/**
 * Grafik pojedynczej sali.
 *
 * Powstał po testach u klienta: grafik nauczyciela pokazuje wyłącznie jego
 * własne zajęcia, więc zajętość sal trzeba było gdzieś zobaczyć. Tutaj widać
 * komplet rezerwacji wraz z prowadzącymi — każdy w swoim kolorze — a kliknięcie
 * w pustą godzinę od razu otwiera formularz rezerwacji.
 *
 * Bloki podpisane są prowadzącym, nie uczniem: przy planowaniu liczy się, kto
 * zajmuje salę. Nazwiska uczniów zostają w grafiku lekcji i w kartotece.
 */
export function RoomSchedulePage() {
  const { roomId = '' } = useParams()

  const [ujecie, setUjecie] = useState<Ujecie>('ogolny')
  const [anchor, setAnchor] = useState(() => new Date())
  const [selected, setSelected] = useState<Lesson | null>(null)
  const [editing, setEditing] = useState<Lesson | null>(null)
  const [editingSeriesId, setEditingSeriesId] = useState<string | null>(null)
  const [preset, setPreset] = useState<{ day: Date; hour: number; roomId?: string } | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [wybranaRezerwacja, setWybranaRezerwacja] = useState<RoomBooking | null>(null)
  const [rezerwacjaOtwarta, setRezerwacjaOtwarta] = useState(false)

  const { user } = useAuth()

  const roomQuery = useQuery({
    queryKey: ['rooms', roomId],
    queryFn: () => roomsApi.get(roomId),
    enabled: roomId !== '',
  })

  const zakres = useMemo(() => ({ from: startOfWeek(anchor), to: endOfWeek(anchor) }), [anchor])

  const ogolny = ujecie === 'ogolny'

  const lessonsQuery = useQuery({
    queryKey: ['schedule', 'room', roomId, toIsoDate(zakres.from)],
    queryFn: () =>
      scheduleApi.lessons({
        date_from: toIsoDate(zakres.from),
        date_to: toIsoDate(zakres.to),
        room_id: roomId,
      }),
    enabled: roomId !== '' && !ogolny,
    placeholderData: keepPreviousData,
  })

  // Rezerwacje pobieramy w obu ujęciach: zajęta sala jest zajęta niezależnie od
  // tego, czy patrzymy na obsadę, czy na poszczególne lekcje.
  const bookingsQuery = useQuery({
    queryKey: ['room-bookings', roomId, toIsoDate(zakres.from)],
    queryFn: () =>
      roomBookingsApi.list({
        date_from: toIsoDate(zakres.from),
        date_to: toIsoDate(zakres.to),
        room_id: roomId,
      }),
    enabled: roomId !== '',
    placeholderData: keepPreviousData,
  })

  const overviewQuery = useQuery({
    queryKey: ['schedule', 'room-overview', roomId, toIsoDate(zakres.from)],
    queryFn: () =>
      scheduleApi.weekOverview({
        date_from: toIsoDate(zakres.from),
        date_to: toIsoDate(zakres.to),
        room_id: roomId,
      }),
    enabled: roomId !== '' && ogolny,
    placeholderData: keepPreviousData,
  })

  const lessons = useMemo(() => lessonsQuery.data ?? [], [lessonsQuery.data])
  const spans = useMemo(() => overviewQuery.data?.spans ?? [], [overviewQuery.data])
  const bookings = useMemo(() => bookingsQuery.data ?? [], [bookingsQuery.data])

  const columns: GridColumn[] = useMemo(
    () =>
      weekDays(anchor).map((day) => ({
        id: toIsoDate(day),
        label: formatDayShort(day),
        highlighted: isToday(day),
        lessons: ogolny
          ? []
          : lessons.filter((lesson) => lesson.starts_at.slice(0, 10) === toIsoDate(day)),
        bookings: bookings.filter((booking) => booking.starts_at.slice(0, 10) === toIsoDate(day)),
        // `day` z serwera jest już datą lokalną, więc porównanie tekstowe wystarczy.
        spans: ogolny ? spans.filter((span) => span.day === toIsoDate(day)) : undefined,
      })),
    [anchor, ogolny, lessons, spans, bookings],
  )

  const prowadzacy = useMemo(() => {
    const wg = new Map<string, string>()
    for (const lesson of lessons) wg.set(lesson.teacher.id, lesson.teacher.full_name)
    for (const span of spans) wg.set(span.teacher.id, span.teacher.full_name)
    return [...wg.entries()].map(([id, name]) => ({ id, name }))
  }, [lessons, spans])

  if (roomQuery.isPending) return <FullScreenLoader label="Wczytywanie sali…" />

  if (roomQuery.isError) {
    const isNotFound = roomQuery.error instanceof ApiError && roomQuery.error.isNotFound
    return (
      <Alert tone="error" title={isNotFound ? 'Nie znaleziono sali' : 'Błąd wczytywania'}>
        {isNotFound ? 'Sala nie istnieje albo nie masz do niej dostępu.' : 'Odśwież stronę.'}{' '}
        <Link to="/sale" className="font-bold text-primary underline">
          Wróć do listy sal
        </Link>
      </Alert>
    )
  }

  const room = roomQuery.data

  return (
    <>
      <Link
        to="/sale"
        className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-on-surface-variant transition-colors hover:text-primary"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Wróć do listy sal
      </Link>

      <PageHeader
        title={room.name}
        description={
          ogolny
            ? `Obsada sali — ${room.location.name}. Kliknij wolną godzinę, aby dodać zajęcia.`
            : `Zajęcia i rezerwacje sali — ${room.location.name}. Kliknij wolną godzinę, aby dodać zajęcia.`
        }
        breadcrumb={['Szkoła', 'Sale', room.name]}
      />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-outline-variant p-4">
          <div className="flex items-center gap-2">
            <IconButton
              label="Poprzedni tydzień"
              icon={<ChevronLeft size={18} />}
              onClick={() => setAnchor((current) => addDays(current, -7))}
            />
            <p className="min-w-56 text-center font-serif text-lg text-on-surface">
              {formatDayShort(zakres.from)} – {formatDayShort(zakres.to)}
            </p>
            <IconButton
              label="Następny tydzień"
              icon={<ChevronRight size={18} />}
              onClick={() => setAnchor((current) => addDays(current, 7))}
            />
            {/* <Button variant="ghost" size="sm" onClick={() => setAnchor(new Date())}>
              Bieżący tydzień
            </Button> */}
          </div>

          <div
            className="flex gap-1 rounded-full bg-surface-container-low p-1"
            role="tablist"
            aria-label="Rodzaj grafiku"
          >
            {UJECIA.map((item) => (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={ujecie === item.value}
                onClick={() => setUjecie(item.value)}
                className={cn(
                  'rounded-full px-4 py-1.5 text-sm font-bold transition-colors',
                  ujecie === item.value
                    ? 'bg-primary text-on-primary'
                    : 'text-on-surface-variant hover:text-primary',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

          {canManageRoomBookings(user) && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Lock size={16} />}
              onClick={() => {
                setPreset({ day: zakres.from, hour: 9, roomId: room.id })
                setRezerwacjaOtwarta(true)
              }}
            >
              Zarezerwuj salę
            </Button>
          )}

          <LegendaProwadzacych teachers={prowadzacy} />
        </div>

        <TimeGrid
          columns={columns}
          colorBy="teacher"
          showStudents={false}
          onSelectLesson={setSelected}
          onSelectBooking={setWybranaRezerwacja}
          onSelectSlot={(columnId, hour) => {
            setPreset({ day: new Date(`${columnId}T00:00:00`), hour, roomId: room.id })
            setIsFormOpen(true)
          }}
          emptyMessage="Brak zajęć w tym tygodniu."
        />
      </Card>

      <LessonDetailModal
        lesson={selected}
        onClose={() => setSelected(null)}
        onEdit={(lesson) => {
          setSelected(null)
          setEditing(lesson)
          setEditingSeriesId(null)
          setIsFormOpen(true)
        }}
        onEditSeries={(seriesId) => {
          setSelected(null)
          setEditing(null)
          setEditingSeriesId(seriesId)
          setIsFormOpen(true)
        }}
      />

      <LessonFormModal
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setPreset(null)
          setEditing(null)
          setEditingSeriesId(null)
        }}
        lesson={editing}
        seriesId={editingSeriesId}
        preset={preset}
        locationId={room.location.id}
      />

      <RoomBookingFormModal
        open={rezerwacjaOtwarta}
        onClose={() => {
          setRezerwacjaOtwarta(false)
          setPreset(null)
        }}
        preset={preset}
        locationId={room.location.id}
      />

      <RoomBookingDetailModal
        booking={wybranaRezerwacja}
        onClose={() => setWybranaRezerwacja(null)}
      />
    </>
  )
}
